import { Text } from '@mantine/core';
import { superstructResolver } from '@hookform/resolvers/superstruct';
import { object as ST, string } from 'superstruct';

import Form from '@/components/content/form/Form';
import MercuryTextInput from '@/components/content/form/components/TextInput';
import Linkv2 from '@/components/content/link/Linkv2';

import { useSendPasswordRecoveryMutation } from '@/services/password';

import type { OnSubmitFunc } from '@/types/form';
import { ForgotPasswordFields, ForgotPasswordLabels } from '../types';

import { required } from '@/utils/inputValidation';
import { PASSWORD_RECOVERY_SENT } from '@/constants/text/english';

interface ForgotPasswordFormProps {
  onBack?: VoidFunction;
}

const ForgotPasswordForm = ({ onBack }: ForgotPasswordFormProps) => {
  const { isPending: isRecoveringPassword, mutate: postSendRecovery } =
    useSendPasswordRecoveryMutation();

  const schema = ST({
    email: required(string(), 'Email is required'),
  });

  const sendRecovery: OnSubmitFunc<ForgotPasswordFields> = (
    formData,
    setGeneralError,
    setSuccessMessage
  ) => {
    postSendRecovery(formData, {
      onSuccess: () => {
        setSuccessMessage(PASSWORD_RECOVERY_SENT, true, { customHtml: true });
      },
      onError: (error) => {
        setGeneralError(error);
      },
    });
  };

  return (
    <>
      {onBack && (
        <Linkv2 as="button" onClick={onBack} type="button">
          Back
        </Linkv2>
      )}

      <Text>
        Please enter the email address you used to register your account. We will send you a link
        that you can use to reset your password.
      </Text>

      <Form<ForgotPasswordFields>
        onSubmit={sendRecovery}
        submitButton={{
          text: 'Reset Password',
          disabled: isRecoveringPassword,
        }}
        options={{ resolver: superstructResolver(schema) }}
        fields={ForgotPasswordLabels}
      >
        {({ register, formState: { errors } }) => (
          <MercuryTextInput<ForgotPasswordFields>
            register={register}
            type="text"
            name="email"
            label={ForgotPasswordLabels.email}
            error={errors?.email?.message}
            required
          />
        )}
      </Form>
    </>
  );
};

export default ForgotPasswordForm;
